<script setup lang="ts">
    import { computed } from 'vue';
    import type { StemSystemGroup } from '@/domain/catalog/useStemSystemGrouping';
    import { kebabCase } from '@/util/format/formatString';
    import {
        PREFERENCES_TARGETS,
        stemSystemOptionTarget,
    } from '@/domain/analytics/analyticsTargets';

    interface Props {
        stemSystemGroups: StemSystemGroup[];
        selectedStemTypesBySystem: Map<string, string[]>;
        preferredSystem: string;
        stemSystems: Array<{ key: string; label: string }>;
        disabled?: boolean;
        showPreferredSystemError?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        disabled: false,
        showPreferredSystemError: false,
    });

    defineEmits<{
        'update:preferredSystem': [value: string];
        'update:selectedStems': [system: string, types: string[]];
    }>();

    const totalSelectedStems = computed(() => {
        let total = 0;
        props.selectedStemTypesBySystem.forEach((types) => {
            total += types.length;
        });
        return total;
    });

    const preferredSystemLabel = computed(() => {
        const system = props.stemSystems.find((s) => s.key === props.preferredSystem);
        return system?.label || props.preferredSystem;
    });
</script>

<template>
    <v-radio-group
        v-if="stemSystemGroups.length > 0"
        :model-value="preferredSystem"
        :disabled="disabled"
        data-testid="stem-system-selection"
        @update:model-value="$event && $emit('update:preferredSystem', $event)"
    >
        <v-alert
            type="info"
            variant="tonal"
            class="mb-4"
            density="comfortable"
            :data-analytics-id="PREFERENCES_TARGETS.stemNotice"
        >
            <div class="text-subtitle-2 font-weight-bold mb-2">Stem System Selection Guide</div>
            <ul class="ml-4">
                <li>
                    <strong>Preferred Stem System:</strong> Select only ONE stem system as your
                    preferred option. This will be prioritized in the 3D planner.
                </li>
                <li>
                    <strong>Secondary Stem Systems:</strong> You can select multiple secondary stem
                    systems as alternatives.
                </li>
                <li>
                    <strong>Important:</strong> Only selected stem types will be shown in the 3D
                    planner page.
                </li>
            </ul>
        </v-alert>
        <div
            v-for="systemGroup in stemSystemGroups"
            :key="systemGroup.system"
            class="stem-system-group mb-4 pa-3 border rounded"
            :data-analytics-id="stemSystemOptionTarget(systemGroup.system)"
            style="border-color: #e0e0e0"
        >
            <v-radio
                :label="'Use ' + systemGroup.systemLabel + ' as your preferred system'"
                :value="systemGroup.system"
                color="primary"
            />
            <div class="ml-8 mt-2">
                <app-chip-group
                    column
                    multiple
                    :model-value="selectedStemTypesBySystem.get(systemGroup.system) || []"
                    :error-messages="
                        systemGroup.system === preferredSystem && showPreferredSystemError
                            ? 'Please select at least one stem from your preferred system'
                            : ''
                    "
                    :data-testid="`group-stem-types-${systemGroup.system}`"
                    @update:model-value="
                        (types: string[]) =>
                            $emit('update:selectedStems', systemGroup.system, types)
                    "
                >
                    <v-chip
                        v-for="typeGroup in systemGroup.typeGroups"
                        :key="typeGroup.type"
                        role="checkbox"
                        class="text-subtitle-1 font-weight-bold"
                        variant="flat"
                        color="primary"
                        :value="typeGroup.type"
                        :filter="true"
                        :text="typeGroup.type"
                        :disabled="disabled"
                        :data-testid="`checkbox-stem-types-${systemGroup.system}-${kebabCase(typeGroup.type)}`"
                    />
                </app-chip-group>
            </div>
        </div>
        <div v-if="preferredSystem && totalSelectedStems > 0" class="stem-selection-summary">
            <div class="text-subtitle-2 font-weight-bold mb-2">Stem Selection Summary</div>
            <div class="d-flex gap-8">
                <span><strong>Preferred System:</strong> {{ preferredSystemLabel }}</span>
                <span><strong>Selected Stem Types:</strong> {{ totalSelectedStems }}</span>
            </div>
        </div>
    </v-radio-group>
</template>

<style scoped>
    .stem-selection-summary {
        margin-top: 0.5rem;
        padding: 0.75rem;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
    }

    .gap-8 {
        gap: 2rem;
    }
</style>
